import { Component, Host, h, Prop, Event, EventEmitter, Element, Fragment } from "@stencil/core";

import { StzhTagType, StzhTagSize, StzhTagClickEvent, StzhTagFocusEvent, StzhTagBlurEvent } from "../../index";

/**
 * @slot - Slot for label
 */
@Component({
  tag: "stzh-tag",
  styleUrl: "stzh-tag.scss",
  scoped: true,
})
export class StzhTag {
  /** Label */
  @Prop() label: string = "";

  /** `href` if the tag should be used as link  */
  @Prop() href: string = "";

  /** Target if the button is used as link (if `href` used)  */
  @Prop() target: string = "";

  /** Whether tag is not interactive */
  @Prop({ reflect: true }) nonInteractive: boolean = false;

  /** Type */
  @Prop({ reflect: true }) type: StzhTagType = "default";

  /** Size */
  @Prop({ reflect: true }) size: StzhTagSize = "default";

  /** Disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Tabindex of link/button */
  @Prop({ attribute: "a11y-tabindex" }) a11yTabindex: string;

  /** Accessible label for screen readers to replace visible text */
  @Prop({ attribute: "a11y-label" }) a11yLabel: string;

  /** Id for element which describes the link/button */
  @Prop({ attribute: "a11y-describedby" }) a11yDescribedby: string = "";

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the link/button element.
   * Default value will be taken from `label` prop or default slot.
   */
  @Prop() analyticsId: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the remove button element.
   * Default value will be taken from `removeLabel` prop.
   */
  @Prop() removeAnalyticsId: string;

  @Element() element: HTMLStzhTagElement;

  /** Click event */
  @Event() stzhClick: EventEmitter<StzhTagClickEvent>;

  /** Focus event */
  @Event() stzhFocus: EventEmitter<StzhTagFocusEvent>;

  /** Input blur event */
  @Event() stzhBlur: EventEmitter<StzhTagBlurEvent>;

  private tag: HTMLButtonElement | HTMLAnchorElement | HTMLDivElement;
  private text: HTMLElement;
  private focusedByInput: boolean = false;

  componentDidRender() {
    requestAnimationFrame(() => {
      if (!this.nonInteractive) {
        this.tag?.setAttribute("s-object-id", this.analyticsId || this.text.innerText);
      }
    });
  }

  private renderInner(): DocumentFragment {
    return (
      <Fragment>
        <stzh-clamp lines={1} class="stzh-tag__label" ref={el => (this.text = el as HTMLElement)}>
          {this.label ? this.label : <slot></slot>}
        </stzh-clamp>
      </Fragment>
    );
  }

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.tag.focus();
    }

    this.focusedByInput = false;
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    const focusEvent = new FocusEvent("focus", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(focusEvent);
    this.stzhFocus.emit({
      component: "stzh-tag",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    const blurEvent = new FocusEvent("blur", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(blurEvent);
    this.stzhBlur.emit({
      component: "stzh-tag",
      originalEvent: event,
    });
  };

  private onClick = (event: MouseEvent) => {
    this.stzhClick.emit({
      component: "stzh-tag",
      originalEvent: event,
      href: this.href,
    });
  };

  render() {
    const classes = {
      "stzh-tag": true,
      "stzh-tag--is-disabled": this.disabled,
      [`stzh-tag--size-${this.size}`]: !!this.size,
      [`stzh-tag--${this.type}`]: !!this.type,
    };

    const Tag = this.nonInteractive ? "div" : this.href ? "a" : "button";

    return (
      <Host tabindex={this.disabled ? null : "-1"} onFocus={this.onRootFocus}>
        <div class={classes}>
          {this.href ? (
            <Tag
              ref={el => (this.tag = el as HTMLAnchorElement)}
              class={{
                "stzh-tag__button": true,
                "is-button": !this.nonInteractive && !this.href,
                "is-link": !this.nonInteractive && !!this.href,
              }}
              href={this.disabled ? null : this.href}
              target={this.target}
              tabindex={this.a11yTabindex}
              aria-label={this.a11yLabel || null}
              aria-describedby={this.a11yDescribedby || null}
              onClick={this.onClick}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
            >
              {this.renderInner()}
            </Tag>
          ) : (
            <Tag
              ref={el => (this.tag = el as HTMLButtonElement | HTMLDivElement)}
              class={{
                "stzh-tag__button": true,
                "is-button": !this.nonInteractive && !this.href,
                "is-link": !this.nonInteractive && !!this.href,
              }}
              disabled={this.disabled}
              type="button"
              tabindex={this.a11yTabindex}
              aria-label={this.a11yLabel || null}
              aria-describedby={this.a11yDescribedby || null}
              onClick={this.onClick}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
            >
              {this.renderInner()}
            </Tag>
          )}
        </div>
      </Host>
    );
  }
}
